import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { isLocale, type Locale } from '@/client/i18n';
import { useMe } from '@/client/hooks/useMe';

export function BookingSubmitted() {
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const bookingId = search.get('bookingId');
  const { data: me } = useMe();

  const steps = locale === 'cs'
    ? [
        'Dajana zkontroluje tvou žádost.',
        'Pokud termín schválí, dostaneš e-mail s odkazem na platbu.',
        'Po platbě ti pošleme kalendářní pozvánku.',
      ]
    : [
        'Dajana skontroluje tvoju žiadosť.',
        'Ak termín schváli, dostaneš e-mail s odkazom na platbu.',
        'Po platbe ti pošleme kalendárovú pozvánku.',
      ];

  return (
    <article className="relative">
      <div
        className="absolute top-10 -right-32 w-[26rem] h-[26rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
        aria-hidden
      />
      <div
        className="absolute top-40 -left-20 w-[24rem] h-[24rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(117,59,189,0.18) 0%, transparent 65%)',
          filter: 'blur(48px)',
          animationDelay: '-10s',
        }}
        aria-hidden
      />

      <section className="relative mx-auto max-w-2xl px-5 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="dr-card-strong p-8 sm:p-12 text-center animate-fade-up">
          <div className="relative mx-auto h-28 w-28 mb-6">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'conic-gradient(from 220deg, rgba(45,139,85,0.30), rgba(193,167,226,0.30), rgba(45,139,85,0.30))',
                filter: 'blur(8px)',
              }}
              aria-hidden
            />
            <img
              src="/brand/mascot-purple.svg"
              alt=""
              className="relative h-full w-full object-contain animate-float"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = '/brand/dajana-purple.svg';
              }}
            />
          </div>

          <div className="inline-flex items-center gap-2 rounded-pill bg-success/10 border border-success/25 text-success px-3 py-1 font-sans text-caption font-medium">
            <CheckCircle2 size={12} />
            {locale === 'cs' ? 'Odesláno' : 'Odoslané'}
          </div>
          <h1 className="mt-4 font-display text-[clamp(1.875rem,4.5vw,2.75rem)] leading-[1.05] tracking-tight text-primary-dark">
            {locale === 'cs' ? 'Žádost odeslaná' : 'Žiadosť odoslaná'}
          </h1>
          <p className="mt-3 font-display text-body-large text-text-secondary leading-relaxed max-w-md mx-auto">
            {locale === 'cs'
              ? 'Dajana ti odpoví do 48 hodin. Dostaneš e-mail s dalšími kroky.'
              : 'Dajana ti odpovie do 48 hodín. Dostaneš e-mail s ďalšími krokmi.'}
          </p>
        </div>

        <div
          className="dr-card-static mt-6 p-6 sm:p-8 text-left animate-fade-up"
          style={{ animationDelay: '140ms' }}
        >
          <p className="dr-eyebrow">
            {locale === 'cs' ? 'Další kroky' : 'Ďalšie kroky'}
          </p>
          <h2 className="mt-2 font-display text-h3 text-primary-dark">
            {locale === 'cs' ? 'Co bude dál?' : 'Čo bude ďalej?'}
          </h2>
          <ol className="mt-5 space-y-3">
            {steps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-4">
                <span className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 font-display text-body font-medium text-primary">
                  {idx + 1}
                </span>
                <span className="font-sans text-body text-text-primary leading-relaxed pt-1">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div
          className="mt-8 flex flex-col items-center gap-4 animate-fade-up"
          style={{ animationDelay: '210ms' }}
        >
          {me ? (
            <Link to={`/${locale}/portal/rezervacie`}>
              <Button size="lg">
                {locale === 'cs' ? 'Otevřít rezervace' : 'Otvoriť rezervácie'}
                <ArrowRight size={16} />
              </Button>
            </Link>
          ) : (
            <>
              <Link to={`/${locale}/registracia?bookingId=${bookingId ?? ''}`}>
                <Button size="lg">
                  {locale === 'cs'
                    ? 'Vytvoř si účet pro sledování'
                    : 'Vytvor si účet pre sledovanie'}
                  <ArrowRight size={16} />
                </Button>
              </Link>
              <p className="font-sans text-caption text-text-muted max-w-md text-center">
                {locale === 'cs'
                  ? 'Bez účtu žádost zůstává platná, ale nedokážeš ji sledovat v portálu.'
                  : 'Bez účtu žiadosť zostáva platná, ale nedokážeš ju sledovať v portáli.'}
              </p>
            </>
          )}

          <Link
            to={`/${locale}`}
            className="mt-2 inline-flex items-center gap-2 font-sans text-body-small text-primary hover:gap-3 transition-all"
          >
            <ArrowLeft size={14} />
            {t('common.back')}
          </Link>
        </div>
      </section>
    </article>
  );
}
